import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faCalendarAlt,
  faImages,
} from '@fortawesome/pro-solid-svg-icons';

import { Event } from '../../../socketProvider';
import PaginationContainer from '../PaginationContainer';
import OverlaidSnapshot from './OverlaidSnapshot';

import { setFavorite } from '../actions';
import { useDelayedRender } from '../../../helpers/hooks';
import { setBannedSnapshots, setBannedUser } from '../../../store/actions/snapshots';
import {
  addSnapshot,
  removeSnapshots,
  setShared,
  setLightBoxSnapshot,
} from './actions';

import style from './style.module.scss';

const AlbumPhotoContainer = (props) => {
  const {
    isFetching,
    error,
    snapshots,
    noEventSelected,
    autoUpdateEnabled,
    sharedDisplayed,
    addSnapshotAction,
    removeSnapshotsAction,
    setFavoriteAction,
    setBannedUserAction,
    setBannedSnapshotsAction,
    setSharedSnapshotAction,
    setLightBoxSnapshotAction,
  } = props;

  // we need this variable to display loading indicator in case of the first render,
  // and skip snapshots render, before we set isFetching prop to true
  const canRender = useDelayedRender();

  const getBody = () => {
    if (!noEventSelected && (isFetching || !canRender)) {
      return (
        <div className={style.LoadingContainer}>
          <Spinner animation="border" variant="primary" />
        </div>
      );
    }

    if (error) {
      console.error('Error fetching media: ', error);
      return (
        <div className={classNames(style.messageContainer, style.error)}>
          <FontAwesomeIcon icon={faExclamationTriangle} className={style.messageIcon} />
          <p className={style.messageText}>Error fetching media</p>
          <p className={style.messageSubtext}>
            Please try again or contact support if the problem persists
          </p>
        </div>
      );
    }

    if (noEventSelected) {
      return (
        <div className={classNames(style.messageContainer, style.info)}>
          <FontAwesomeIcon icon={faCalendarAlt} className={style.messageIcon} />
          <p className={style.messageText}>Please select an event</p>
          <p className={style.messageSubtext}>Choose an event from the dropdown to view media</p>
        </div>
      );
    }

    if (snapshots.length === 0) {
      return (
        <div className={classNames(style.messageContainer, style.empty)}>
          <FontAwesomeIcon icon={faImages} className={style.messageIcon} />
          <p className={style.messageText}>No media available</p>
          <p className={style.messageSubtext}>There are no photos or videos for this event yet</p>
        </div>
      );
    }

    const selectionMode = snapshots.some((i) => i.selected);

    return (
      <div className={classNames(style.photos, 'row', 'album-photos', { [style.selectionMode]: selectionMode })}>
        {snapshots.map((snapshot) => (
          <OverlaidSnapshot
            key={snapshot._id}
            snapshot={snapshot}
            sharedDisplayed={sharedDisplayed}
            onClick={setLightBoxSnapshotAction}
          />
        ))}
      </div>
    );
  };

  const onBannedEvent = useCallback((bannedData) => {
    if (Array.isArray(bannedData)) {
      setBannedSnapshotsAction(bannedData);
    } else {
      setBannedUserAction(bannedData);
    }
  });

  return (
    <div className={style.AlbumPhotoContainer}>
      {getBody()}
      <PaginationContainer />

      {autoUpdateEnabled && (
        <Event event="monitor newSnapshot" handler={addSnapshotAction} />
      )}

      <Event event="monitor snapshotDeleted" handler={removeSnapshotsAction} />
      <Event event="monitor favoritedSnapshots" handler={setFavoriteAction} />
      <Event event="monitor bannedChanged" handler={onBannedEvent} />
      <Event event="monitor snapshotShared" handler={setSharedSnapshotAction} />
    </div>
  );
};

export default connect(
  ({
    snapshots: {
      isFetching,
      error,
      items,
    },
    events: {
      selectedEvents,
    },
    album: {
      options: {
        autoUpdateEnabled,
        sharedDisplayed,
      },
    },
  }) => ({
    isFetching,
    error,
    snapshots: items,
    autoUpdateEnabled,
    sharedDisplayed,
    noEventSelected: selectedEvents.length === 0,
  }),
  {
    addSnapshotAction: addSnapshot,
    removeSnapshotsAction: removeSnapshots,
    setFavoriteAction: setFavorite,
    setBannedUserAction: setBannedUser,
    setBannedSnapshotsAction: setBannedSnapshots,
    setSharedSnapshotAction: setShared,
    setLightBoxSnapshotAction: setLightBoxSnapshot,
  },
)(AlbumPhotoContainer);
